import { useAtomValue } from 'jotai';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { useTranslate } from '@growtherapy/i18n';

import { useAtomCallbackMemoized } from '../../hooks/useAtomCallbackMemoized';
import { useGetTelehealthSessionInfo } from '../../hooks/useGetTelehealthSessionInfo';
import { useTrackEvent } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';
import { providerConsentAtom } from '../../session/messages/state';
import { ClientTranscriptionUserAction } from '../../session/types';
import { sessionInfoAtom } from '../../state';
import { toast } from '../../toasts';
import { UserType } from '../../types';
import { ConsentStatus, participantCountAtom } from '../../video-sdk';
import { useSendVisitorParticipantStateCallback } from '../messages/useSendVisitorParticipantState';
import { ConsentModal } from './ConsentModal';
import { useGracefullyRecordClientTranscriptionConsentCallback } from './useGracefullyRecordClientTranscriptionConsentCallback';
import { useShowRefreshedClientConsentCopy } from './useShowRefreshedClientConsentCopy';
import { useToggleRecordingCallback } from './useToggleRecordingCallback';

enum RecordConsentResponse {
  'CONSENTED',
  'ERROR',
  'CONSENTED_AND_RECORDING',
}

function useRecordConsentCallback() {
  const t = useTranslate();
  const { userClicked } = useTrackEvent();
  const getSessionInfo = useAtomCallbackMemoized((get) => get(sessionInfoAtom), []);
  const getProviderConsent = useAtomCallbackMemoized((get) => get(providerConsentAtom).value, []);
  const toggleRecording = useToggleRecordingCallback();
  const { recordClientConsentCallback: recordConsent, isLoading } =
    useGracefullyRecordClientTranscriptionConsentCallback();

  const recordConsentCallback = useCallback(
    async (isOptedIn: boolean, userAction?: ClientTranscriptionUserAction) => {
      const sessionInfo = getSessionInfo();
      const appointmentShortId = sessionInfo?.appointment?.shortId;
      userClicked(EventTypeEnum.GAVE_TRANSCRIPTION_CONSENT, {
        appointmentShortId: appointmentShortId!,
        entityType: UserType.CLIENT,
      });

      const recordingResult = await recordConsent(isOptedIn, userAction);

      if (recordingResult.status === 'failure') {
        toast.error(t('telehealth-core:consent.anUnexpectedErrorOccurredWhileTryingToRecordYourConsent'));
        return RecordConsentResponse.ERROR;
      }

      if (recordingResult.status === 'success' && isOptedIn) {
        // Start recording if provider has consented
        const providerConsent = getProviderConsent();
        if (providerConsent === ConsentStatus.OPTED_IN) {
          toggleRecording(true);
          return RecordConsentResponse.CONSENTED_AND_RECORDING;
        }
      }

      return RecordConsentResponse.CONSENTED;
    },
    [getProviderConsent, getSessionInfo, recordConsent, t, toggleRecording, userClicked],
  );

  return {
    recordConsentCallback,
    isLoading,
  };
}

export function ConsentModalWrapper({
  onClose,
  onStartRecording,
  setShouldShowClientConsentDeclinedToast,
  isOpen,
  setPatientConsent,
}: {
  onClose: () => void;
  onStartRecording: () => void;
  setShouldShowClientConsentDeclinedToast: (shouldShow: boolean) => void;
  isOpen: boolean;
  setPatientConsent: ({ value, lastUpdated }: { value: ConsentStatus; lastUpdated: number }) => void;
}) {
  const t = useTranslate();
  const participantCount = useAtomValue(participantCountAtom);
  const { providerShortId, patientShortId } = useParams();
  const { data } = useGetTelehealthSessionInfo(providerShortId, patientShortId);
  const providerName = data?.telehealthSessionInfo?.provider?.name ?? t('telehealth-core:consent.yourProvider');
  const showRefreshedCopy = useShowRefreshedClientConsentCopy(patientShortId);
  const sendParticipantState = useSendVisitorParticipantStateCallback();

  const { recordConsentCallback: recordConsent, isLoading } = useRecordConsentCallback();
  const onConsent = useCallback(async () => {
    recordConsent(true).then((consentResult) => {
      if (consentResult === RecordConsentResponse.CONSENTED_AND_RECORDING) {
        onStartRecording();
      }
      if (consentResult !== RecordConsentResponse.ERROR) {
        onClose();
      }
    });
  }, [recordConsent, onStartRecording, onClose]);

  // Dismiss modal and set consent to SOFT_NO and show toast
  const declineConsent = useCallback(() => {
    setPatientConsent({
      value: ConsentStatus.SOFT_NO,
      lastUpdated: Date.now(),
    });
    recordConsent(false, ClientTranscriptionUserAction.REJECT).then((consentResult) => {
      if (consentResult !== RecordConsentResponse.ERROR) {
        sendParticipantState();
        setShouldShowClientConsentDeclinedToast(true);
        onClose();
      }
    });
  }, [sendParticipantState, setPatientConsent, recordConsent, setShouldShowClientConsentDeclinedToast, onClose]);

  // Dismiss modal and set consent to SOFT_NO WITHOUT showing toast
  const dismissConsent = useCallback(() => {
    // Guard against this being called if the modal is not open, which
    // technically may happen if the Sprout modal is closed but still computed
    // to render with its internal state.
    if (!isOpen) return;
    setPatientConsent({
      value: ConsentStatus.SOFT_NO,
      lastUpdated: Date.now(),
    });
    recordConsent(false, ClientTranscriptionUserAction.DISMISS).then((consentResult) => {
      if (consentResult !== RecordConsentResponse.ERROR) {
        sendParticipantState();
        setShouldShowClientConsentDeclinedToast(true);
        onClose();
      }
    });
  }, [
    sendParticipantState,
    setPatientConsent,
    recordConsent,
    setShouldShowClientConsentDeclinedToast,
    onClose,
    isOpen,
  ]);

  return (
    <ConsentModal
      onDismissConsent={dismissConsent}
      onDeclineConsent={declineConsent}
      isLoading={isLoading}
      isOpen={isOpen}
      providerName={providerName}
      isMultiPatientSession={participantCount > 1}
      showRefreshedCopy={showRefreshedCopy}
      onConsent={onConsent}
    />
  );
}
